import { stringify } from 'qs';
import i18next from 'i18next';
import axios from 'axios';
import { StatusCodes } from 'http-status-codes';
import IApiResponse from '../../../interfaces/IApiResponse';
import { getRoundManagerApiAxiosInstance } from '../../axios-config';
import IPagination from '../../../interfaces/IPagination';
import ILoadListData from '../../../interfaces/loads/ILoadListData';
import IFiltersModel from '../../../interfaces/IFiltersModel';

export enum PoolStatus {
    BOTH = 'Both',
    HAS_RATE = 'HasRate',
    NO_RATE = 'NoRate',
}

export interface IPoolPagination<T> extends IPagination<T> {
    loadsWithoutRateCount: number;
}

const getPoolLoads = async (
    page: number,
    pageSize: number = 30,
    filters?: IFiltersModel,
): Promise<IApiResponse<IPoolPagination<ILoadListData>>> => {
    try {
        const Api = await getRoundManagerApiAxiosInstance();

        const request = {
            _page: page,
            _size: pageSize,
            LoadId: filters?.loadId,
            PoolStatus: mapLoadStatusToPoolStatus(filters?.loadStatus),
            Channels: filters?.channels,
            InitialCollectionDate: filters?.initialCollectionDate,
            EndingCollectionDate: filters?.endingCollectionDate,
            OriginFederativeUnit: filters?.originFederativeUnit,
            OriginLocations: filters?.originLocations,
            DestinationFederativeUnit: filters?.destinationFederativeUnit,
            DestinationLocations: filters?.destinationLocations,
            MaterialId: filters?.product,
        };

        const response = await Api.get('v1/pool/loads:available', {
            params: request,
            paramsSerializer(params: any) {
                return stringify(params, { allowDots: true });
            },
        });

        return {
            response: (response.data || {}) as IPoolPagination<ILoadListData>,
        };
    } catch {
        return { error: new Error(i18next.t('CargasErroConsultar')) };
    }
};

const mapLoadStatusToPoolStatus = (
    loadStatus?: string[],
): PoolStatus | undefined => {
    if (!loadStatus || loadStatus.length === 0) {
        return undefined;
    }

    const hasWaitingConfirmation = loadStatus.includes('WaitingConfirmation');
    const hasNoRate = loadStatus.includes('NoRate');

    if (hasWaitingConfirmation && hasNoRate) {
        return PoolStatus.BOTH;
    }
    if (hasWaitingConfirmation) {
        return PoolStatus.HAS_RATE;
    }
    if (hasNoRate) {
        return PoolStatus.NO_RATE;
    }

    return PoolStatus.BOTH;
};

const confirmPoolLoads = async (
    loads: string[],
    vehicleName: string | null = null,
): Promise<IApiResponse<string[]>> => {
    try {
        const Api = await getRoundManagerApiAxiosInstance();

        const response = await Api.post('v1/pool/loads:confirm', {
            loadIds: loads,
            vehicleName,
        });

        return {
            response: (response.data || []) as string[],
            statusCode: StatusCodes.OK as number,
        };
    } catch (error: any) {
        if (axios.isAxiosError(error)) {
            if (error.response?.status === StatusCodes.CONFLICT) {
                return {
                    response: (error.response.data || []) as string[],
                    statusCode: StatusCodes.CONFLICT as number,
                };
            }
        }
        return { error: new Error(i18next.t('ErroConfirmarCarga')) };
    }
};

// eslint-disable-next-line import/prefer-default-export
export { getPoolLoads, confirmPoolLoads };
